import { el } from "@dhx/ts-common/dom";
import { getWidth } from "../helpers/cells";
import { removeHTMLTags, transpose } from "./../helpers/main";
import { GridEvents, ICol, IFixedRowsConfig, IRendererConfig } from "./../types";
import { content } from "./content";

function handleMouse(col: ICol, config: IRendererConfig, type: any, e: any) {
	config.events.fire(type, [col, e]);
}
function getHandlers(column: ICol, rowName: string, config: IRendererConfig) {
	return {
		onclick: [handleMouse, column, config, GridEvents[rowName + "CellClick"]],
		onmouseover: [handleMouse, column, config, GridEvents[rowName + "CellMouseOver"]],
		onmousedown: [handleMouse, column, config, GridEvents[rowName + "CellMouseDown"]],
		ondblclick: [handleMouse, column, config, GridEvents[rowName + "CellDblClick"]],
		oncontextmenu: [handleMouse, column, config, GridEvents[rowName + "CellRightClick"]]
	};
}

function buildRows(columns: ICol[], name: string): any[][] {
	const header = columns.map(col => col[name] || [{}]);
	return transpose(header);
}

function getCustomContentCell(cell: any, column: ICol, config: IRendererConfig, rowName: string, css: string = "") {
	const rowHeight = (config[`${rowName}RowHeight`] - 10 + 1) || 31;
	const type = column.type ? `dhx_${column.type}-cell` : "dhx_string_cell";
	// TODO: over with index of filter inside of header or footer
	return el(`.dhx_grid-${rowName}-cell.dhx_grid-custom-content-cell.${type}`, {
		class: css,
		style: {
			width: column.width,
			lineHeight: rowHeight + "px"
		},
		...getHandlers(column, rowName, config)
	}, [
			content[cell.content] && content[cell.content].toHtml(column, config)
		]);
}
export function getRows(config: IRendererConfig, rowsConfig: IFixedRowsConfig) {
	if (!config.data || !config.columns) {
		return [];
	}
	const rowName = rowsConfig.name;
	const columns = config.currentColumns;

	const rowHeight = config[`${rowName}RowHeight`] || 40;

	const rows = buildRows(columns, rowName);
	return rows.map((row, j) => el(`.dhx_${rowName}-row`, { style: { height: rowHeight } }, row.map((cell, i) => {
		let css = cell.css || "";
		const column = columns[i];
		let sortIconCss = "dxi dxi-sort-variant dhx_grid-sort-icon";

		if (config.sortBy && `${column.id}` === config.sortBy && !cell.content) {
			sortIconCss += ` dhx_grid-sort-icon--${config.sortDir || "asc"}`;
			css += ` dhx_grid-${rowName}-cell--sorted `;
		}

		const sortIconVisible = cell.text && config.headerSort && rowName !== "footer";
		// [todo]
		if (sortIconVisible) {
			css += " dhx_grid-header-cell--sortable";
		}
		const isFirstCol = i === 0 ? "dhx_first-column-cell" : "";

		const isLastCol = i === columns.length - 1 ? "dhx_last-column-cell" : "";

		if (!cell.content) {
			css += ` dhx_grid-header-cell--${column.type === "number" ? "align_right" : "align_left"} `;
		}

		css += `${isFirstCol} ${isLastCol}`;

		let resizable: any = column.resizable !== undefined ? column.resizable : config.resizable;
		if (resizable) {
			resizable = el("div", {class: "dhx_resizer_grip_wrap"}, [
				el("div", {
					class: "dhx_resizer_grip",
					dhx_resized: column.id,
					style: {
						height: `${rows.length * 100}%`
					}
				}, [
					el("div", {class: "dhx_resizer_grip_line"})
				])
			]);
			if (rowName === "footer" || j > 0) {
				resizable = null;
			}
		}

		if (cell.content) {
			return getCustomContentCell(cell, column, config, rowName, css);
		}
		return !column.hidden ? el(`.dhx_grid-${rowName}-cell`, {
			class: css.trim(),
			dhx_id: column.id,
			_key: i,
			style: {
				width: column.width,
				lineHeight: rowHeight + 1 + "px"
			},
			...getHandlers(column, rowName, config),
			title: removeHTMLTags(cell.text),
		}, [
				el("div", {
					class: "dhx_grid-header-cell-text"
				}, [
					el("div", {
					".innerHTML": cell.text
					}), resizable || null
				]),
				sortIconVisible && el("div", { class: sortIconCss })
			]) : null;
	})));
}

export function getSpans(config: IRendererConfig, rowsConfig: IFixedRowsConfig) {
	const cols = config.columns;
	const rows = transpose(cols.map(col => col[rowsConfig.name] || []));
	const height = config[`${rowsConfig.name}RowHeight`] || 40;

	let leftShift = 0;
	return rows.map((row, i) => {
		leftShift = 0;
		return el(".dhx_span-row", { style: { top: height * i + "px", height } },
			row.map((cell, cellIdx) => {
				const col = cols[cellIdx];

				leftShift += col.hidden ? 0 : col.width;
				const isFirstCol = cellIdx === 0 ? "dhx_first-column-cell" : "";

				const isLastCol = (cellIdx === cols.length - 1)
					|| ((cell.colspan || 0) + (cellIdx - 1) >= cols.length - 1) ? "dhx_last-column-cell" : "";
				let spanHeight = height;

				if (cell.rowspan) {
					spanHeight = spanHeight * cell.rowspan - 1;
				}
				const sortIconVisible =
					cell.rowspan
					&& cell.text
					&& config.headerSort
					&& rowsConfig.name !== "footer";

				let sortIconCss = "dxi dxi-sort-variant dhx_grid-sort-icon";

				if (config.sortBy && `${col.id}` === config.sortBy && !cell.content) {
					sortIconCss += ` dhx_grid-sort-icon--${config.sortDir || "asc"}`;
				}
				let css = `${isFirstCol} ${isLastCol} ${cell.rowspan ? "dhx_span-cell__rowspan" : ""}`;
				if (sortIconVisible) {
					css += " dhx_grid-header-cell  dhx_grid-header-cell--sortable";
				}
				if (!cell.content) {
					css += ` dhx_grid-header-cell--${col.type === "number" ? "align_right" : "align_left"} `;
				}
				let borderLeft = "";
				if (leftShift - col.width > 0) {
					borderLeft = "1px solid #e4e4e4";
				}

				return (cell.colspan || cell.rowspan) && !col.hidden ?
					el(".dhx_span-cell", {
						style: {
							width: getWidth(config.columns, cell.colspan, cellIdx),
							height: spanHeight,
							left: leftShift - col.width,
							borderLeft,
							top: height * i,
							lineHeight: spanHeight + "px"
						},
						class: css.trim(),
						title: removeHTMLTags(cell.text),
						dhx_id: col.id
					}, [
							el("div", { ".innerHTML": cell.text }),
							sortIconVisible && el("div", { class: sortIconCss })
						])
					: null;
			}).filter(cell => cell)
		);
	});
}

export function getFixedRows(config: IRendererConfig, rowsConfig: IFixedRowsConfig) {

	const rows = getRows(config, rowsConfig);
	const spans = getSpans(config, rowsConfig);
	let fixedCols = null;
	if (rowsConfig.name === "footer" && !rowsConfig.sticky) {
		fixedCols = config.splitAt >= 0 && getRows(
			{
				...config,
				currentColumns: config.columns.slice(0, config.splitAt),
				$positions: { ...config.$positions, xStart: 0, xEnd: config.splitAt }
			},
			rowsConfig);
	}
	const styles = {
		position: "sticky",
		[rowsConfig.position]: 0,
	};
	let left;
	if (!rowsConfig.sticky) {
		styles.left = -config.scroll.left;
		left = -config.scroll.left;
		styles.position = "relative";
	}
	const BORDERS = 2;
	return el(`.dhx_${rowsConfig.name}-wrapper`, {
		class: rowsConfig.sticky ? "" : `dhx_compatible-${rowsConfig.name}`,
		style: {
			...styles,
			left: rowsConfig.sticky ? left : 0,
			height: config[rowsConfig.name + "Height"],
			width: rowsConfig.sticky ? config.$totalWidth : rowsConfig.wrapper.width - BORDERS,
		}
	}, [
			el(`.dhx_grid-${rowsConfig.name}`, {
				style: {
					height: config[rowsConfig.name + "Height"],
					left,
					paddingLeft: rowsConfig.shifts.x,
					width: config.$totalWidth,

				}
			}, [
					el(`.dhx_${rowsConfig.name}-rows`, [
						...rows
					]),
					el(`.dhx_${rowsConfig.name}-spans`, {
						style: {
							marginLeft: -rowsConfig.shifts.x
						}
					}, spans),
					fixedCols && el(`.dhx_${rowsConfig.name}-fixed-cols`, {
						style: {
							position: "absolute",
							top: 0,
							left: config.scroll.left + "px",
							height: "100%"
						}
					}, fixedCols)
				]),
			el("div", { style: { width: config.$totalWidth } })
		]
	);
}